import { useAuth, useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { releaseCampaignBuilderDraft, UB_RECIPIENTS_IMG } from "@/constants/campaignBuilder";
import { TOUR_COPY } from "@/constants/tour";
import { useTour } from "@/context/TourContext";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useOnboardingTourSeen } from "@/hooks/useOnboardingTourSeen";
import { usePermissions } from "@/hooks/usePermissions";
import { isTourEligible } from "@/utils/tour-helper";

/**
 * WelcomeOverlay — frosted glass card shown once per user, ever, on their first dashboard visit.
 *
 * Eligibility is the per-user `onboarding_tour_seen` flag on Clerk `unsafeMetadata` (via
 * `useOnboardingTourSeen`), not sessionStorage — it survives logout/login and never re-opens.
 * `pages/Dashboard.tsx` still reads the separate `gfg_new_account` sessionStorage flag for its own
 * entrance animation; that flag is no longer this component's trigger.
 *
 * The overlay stays mounted for the component's whole lifetime and only toggles the `.open`
 * class, instead of mounting/unmounting, so its CSS opacity/blur transition can actually play
 * both ways. That matters once the tour is launched from here: the card hides but the frosted
 * glass stays up as the tour's backdrop, then fades out through that same transition when the
 * tour ends.
 */
export function WelcomeOverlay() {
  const { user } = useUser();
  const { actor } = useAuth();
  const navigate = useNavigate();
  const { isLoaded, isGfg, can } = usePermissions();
  const isMobile = useIsMobile();
  const { hasSeen, markSeen } = useOnboardingTourSeen();
  const {
    isOpen: isTourOpen,
    startedFromWelcome,
    dismissedWelcomeThisSession,
    start,
    markWelcomeDismissed,
  } = useTour();

  // The card and the tour have DIFFERENT mobile rules, so they are gated separately.
  //
  // The tour is disabled below 768px because the sidebar is a closed drawer there, so steps 2 and
  // 3 would have no target to light up. The card has no such problem — it is just a card — so a
  // client who first signs in on a phone is still welcomed, and still gets the flag written when
  // they dismiss it. Only the tour entry point is withheld. Passing `isMobile: false` here is
  // deliberate: it asks the shared predicate every question EXCEPT the mobile one.
  const canSeeTour = isTourEligible({
    isLoaded,
    isGfg,
    isImpersonating: !!actor,
    isMobile,
    canEditCompany: can("edit", "COMPANY"),
  });
  const canSeeWelcome = isTourEligible({
    isLoaded,
    isGfg,
    isImpersonating: !!actor,
    isMobile: false,
    canEditCompany: can("edit", "COMPANY"),
  });

  const open = canSeeWelcome && !hasSeen && !dismissedWelcomeThisSession;

  // Once launched from here, the tour keeps the glass up as its backdrop even after `open`
  // above goes false (markSeen flips `hasSeen`, or the tour navigates off /dashboard).
  const runningFromWelcome = isTourOpen && startedFromWelcome;

  const firstName = (user?.firstName ?? "").trim();

  const handleTakeTour = () => {
    void markSeen();
    start({ fromWelcome: true });
  };

  const handleStartSending = () => {
    void markSeen();
    markWelcomeDismissed();
    releaseCampaignBuilderDraft();
    navigate("/campaign/create");
  };

  return (
    <div
      className={`gfg-welcome-overlay ${open || runningFromWelcome ? "open" : ""}`}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`gfg-welcome-card ${runningFromWelcome ? "opacity-0 pointer-events-none" : ""}`}
      >
        <img
          src={UB_RECIPIENTS_IMG}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="block w-[168px] max-w-[62%] h-auto mx-auto mb-[18px]"
        />
        <h2>{TOUR_COPY.welcomeHeading(firstName)}</h2>
        <p>{TOUR_COPY.welcomeBody}</p>
        <div className="gfg-welcome-actions">
          {canSeeTour && (
            <button type="button" className="is-primary" onClick={handleTakeTour}>
              {TOUR_COPY.welcomeTakeTour}
            </button>
          )}
          {/* On mobile this is the only action, so it becomes the primary one. */}
          <button
            type="button"
            className={canSeeTour ? undefined : "is-primary"}
            onClick={handleStartSending}
          >
            {TOUR_COPY.welcomeStartSending}
          </button>
        </div>
      </div>
    </div>
  );
}
